import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { simulationService } from '@services/simulationService';
import toast from 'react-hot-toast';

const QUERY_KEY = 'simulations';

export const useSimulations = () => {
  const queryClient = useQueryClient();

  const {
    data: simulations = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [QUERY_KEY],
    queryFn: simulationService.getAll,
  });

  const createMutation = useMutation({
    mutationFn: simulationService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success('Simulação criada com sucesso!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao criar simulação');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ simulationId, data }) => simulationService.update(simulationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success('Simulação atualizada!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao atualizar');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: simulationService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success('Simulação excluída!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao excluir');
    },
  });

  const addEventMutation = useMutation({
    mutationFn: ({ simulationId, event }) => simulationService.addEvent(simulationId, event),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success('Evento adicionado!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao adicionar evento');
    },
  });

  const removeEventMutation = useMutation({
    mutationFn: ({ simulationId, eventId }) => simulationService.removeEvent(simulationId, eventId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success('Evento removido!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao remover evento');
    },
  });

  return {
    simulations,
    isLoading,
    error,
    refetch,
    createSimulation: createMutation.mutateAsync,
    updateSimulation: updateMutation.mutateAsync,
    deleteSimulation: deleteMutation.mutateAsync,
    addEvent: addEventMutation.mutateAsync,
    removeEvent: removeEventMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};

export const useSimulationCalculation = (simulationId) => {
  return useQuery({
    queryKey: ['simulations', simulationId, 'calculate'],
    queryFn: () => simulationService.calculate(simulationId),
    enabled: !!simulationId,
  });
};

export const useSimulationComparison = (simulationIds) => {
  return useQuery({
    queryKey: ['simulations', 'compare', simulationIds],
    queryFn: () => simulationService.compare(simulationIds),
    enabled: simulationIds.length > 0,
  });
};

export default useSimulations;
